import React from 'react';
import { useSelector } from 'react-redux';
import { selectUserIsAuthenticated } from 'core/selectors/user';
import { selectSiteUrl, selectTermsAndConditionsURL } from 'core/selectors/app';
import { CARDS_LIMIT } from 'core/modules/Instore2/utils/constants';
import { selectDynamicCardRemoveFeedback, selectDynamicCardLinksIsLoading } from 'core/modules/Instore2/selectors';
import { clearCardDeleteFailure } from 'core/modules/Instore2/actions';
import { useGetPaymentCards } from 'core/modules/Instore2/hooks/useGetPaymentCards';
import { useScrollToFaq } from 'core/modules/Instore2/hooks/useScrollToFaq';
import { useApiFeedbackManager } from 'core/hooks/useApiFeedbackManager';
import Modal from 'core/modules/Modal/Modal';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import Loader from 'core/modules/Partials/Loader/Loader';
import SignInModal from 'core/modules/Instore2/components/Modals/SignInModal/SignInModal';
import { AddCardButton, RemoveCardWarning, Disclaimer } from 'core/modules/Instore2/partials';
import ManageCard from './components/ManageCard/ManageCard';
import SupportedCards from './components/SupportedCards/SupportedCards';
import CardsLimitWarning from './components/CardsLimitWarning/CardsLimitWarning';
import config from './configs';

import './ManageCardsModal.scss';

function ManageCardsModal() {
  const {
    getTitle, getSubTitle, shouldShowSupportedCards, addCardButtonText,
  } = config;
  const {
    cards, isLoading, availableCards, checkCardIsDynamicallyDeleted,
  } = useGetPaymentCards();
  const { onFaqClick } = useScrollToFaq();
  const siteUrl = useSelector(selectSiteUrl);
  const isUserAuthenticated = useSelector(selectUserIsAuthenticated);
  const dynamicCardRemoveFeedback = useSelector(selectDynamicCardRemoveFeedback);
  const termsAndConditionsURL = useSelector(selectTermsAndConditionsURL);

  // The loader should be also shown from the moment when user added a new card and until this card is
  // autlinked (if it is allowed for current user) to all already linked offers and updated offers are re-fetched
  const dynamicCardLinksIsLoading = useSelector(selectDynamicCardLinksIsLoading);

  const { shouldShowFeedback, clearFeedback, feedbackData } = useApiFeedbackManager({
    data: dynamicCardRemoveFeedback,
    callbackAction: clearCardDeleteFailure,
  });

  if (!isUserAuthenticated) {
    return <SignInModal />;
  }

  const title = getTitle();
  const subTitle = getSubTitle(onFaqClick);
  const isCardLimitReached = availableCards.length >= CARDS_LIMIT;

  return (
    <Modal className="mn_instoreModal mn_manageCardsModal" options={{ shouldUpdateUrl: true }}>
      <SectionTitle id="mn_dialogTitle" title={title} />
      <div id="mn_dialogDescription" className="mn_info">{subTitle}</div>
      <SupportedCards shouldShowSupportedCards={shouldShowSupportedCards} />
      <RemoveCardWarning hasError={shouldShowFeedback} feedbackData={feedbackData} />
      <div className="mn_cards">
        <Loader isLoading={isLoading || dynamicCardLinksIsLoading}>
          {cards.map((card) => (
            <ManageCard
              key={card.cardId}
              card={card}
              shouldShowWarning={shouldShowFeedback}
              clearWarning={clearFeedback}
              isCardDeleted={checkCardIsDynamicallyDeleted(card.cardId)}
            />
          ))}
        </Loader>
      </div>
      <CardsLimitWarning shouldShowWarning={isCardLimitReached} />
      <AddCardButton isDisabled={isLoading || isCardLimitReached}>{addCardButtonText}</AddCardButton>
      <Disclaimer>
        For debit cards, remember to run them as a credit card at checkout (do not use your PIN).
        See <a href={`${siteUrl}${termsAndConditionsURL}#instore`}>Terms and conditions</a> for full
        card and transaction eligibility details.
      </Disclaimer>
    </Modal>
  );
}

export default (props) => <ComponentErrorBoundary><ManageCardsModal {...props} /></ComponentErrorBoundary>;
